import { useCallback, useEffect, useState } from 'react'
import type { AsyncSearchSelectOption } from '@ideon-api/ideon-ui'
import { sdkErrorMessage, useAuthServiceClient } from './sdk-client'
import { useCsvExport } from './use-csv-export'

type Client = Awaited<ReturnType<ReturnType<typeof useAuthServiceClient>>>
type SearchRow = NonNullable<Awaited<ReturnType<Client['auth']['searchAccounts']>>['data']>[number]

export interface AccountListItem {
  id: string
  slug: string
  name: string
  createdAt: string
  archived: boolean
  permissionSetCount?: number
  licensing?: SearchRow['licensing']
}

export interface LicensingFilters {
  year?: number
  quarter?: number
  productLine?: string
  state?: string
  licensedOnly?: boolean
}

export interface SearchMeta {
  page: number
  perPage: number
  totalCount: number
  totalPages: number
}

export type SearchState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; data: AccountListItem[]; meta: SearchMeta; pending?: boolean }

// Empty query returns AccountSearch's default relation -- most-recently-
// created-first, excluding archived accounts as of PD-30167's backend MR
// (`Account.active`) -- unless `includeArchived` is set. There's always
// something to browse, query or not.
//
// `reload()` mirrors `useSdkList`'s pattern (a bumped key re-runs the
// effect) -- callers that mutate an account shown in this list (e.g. a merge
// archiving the loser) need a way to refetch without changing query/page.
export function useAccountSearch(
  query: string,
  page: number,
  perPage: number,
  includeArchived = false,
  filters: LicensingFilters = {}
): SearchState & { reload: () => void } {
  const getClient = useAuthServiceClient()
  const [state, setState] = useState<SearchState>({ status: 'loading' })
  const [reloadKey, setReloadKey] = useState(0)
  const { year, quarter, productLine, state: usState, licensedOnly } = filters

  useEffect(() => {
    let cancelled = false
    // Keep the last results on screen while refetching, so the page does not jump.
    setState((prev) => (prev.status === 'ready' ? { ...prev, pending: true } : { status: 'loading' }))

    getClient()
      .then((client) => client.auth.searchAccounts({
          q: query || undefined,
          page,
          perPage,
          includeArchived,
          year,
          quarter,
          productLine,
          state: usState,
          licensedOnly: licensedOnly || undefined,
        }))
      .then((response) => {
        if (cancelled) return
        const data: AccountListItem[] = (response.data ?? []).map((row) => ({
          id: row.id ?? '',
          slug: row.slug ?? '',
          name: row.name ?? '',
          createdAt: row.createdAt ?? '',
          archived: row.archived,
          permissionSetCount: row.permissionSetCount ?? 0,
          licensing: row.licensing,
        }))
        setState({
          status: 'ready',
          data,
          meta: {
            page: response.meta?.page ?? page,
            perPage: response.meta?.perPage ?? perPage,
            totalCount: response.meta?.totalCount ?? data.length,
            totalPages: response.meta?.totalPages ?? 1,
          },
        })
      })
      .catch((error: unknown) => {
        if (!cancelled) setState({ status: 'error', message: sdkErrorMessage(error, 'Failed to search accounts.') })
      })

    return () => {
      cancelled = true
    }
  }, [getClient, query, page, perPage, includeArchived, year, quarter, productLine, usState, licensedOnly, reloadKey])

  return { ...state, reload: () => setReloadKey((k) => k + 1) }
}

// One-shot search for a combobox: the first 20 active accounts matching the text.
export function useAccountOptionSearch(): (query: string) => Promise<AsyncSearchSelectOption[]> {
  const getClient = useAuthServiceClient()
  return useCallback(
    async (query: string) => {
      const client = await getClient()
      const response = await client.auth.searchAccounts({ q: query || undefined, page: 1, perPage: 20 })
      return (response.data ?? [])
        .filter((row) => row.slug)
        .map((row) => ({ value: row.slug ?? '', label: row.name ?? '', description: row.slug ?? '' }))
    },
    [getClient]
  )
}

export interface ExportFilters extends LicensingFilters {
  q?: string
  includeArchived?: boolean
}

const exportRequest = (token: string, filters: ExportFilters) =>
  fetch('/api/v1/accounts/search/export', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      q: filters.q || undefined,
      include_archived: filters.includeArchived || undefined,
      year: filters.year,
      quarter: filters.quarter,
      product_line: filters.productLine || undefined,
      state: filters.state || undefined,
      licensed_only: filters.licensedOnly || undefined,
    }),
  })

export const useExportAccounts = () => useCsvExport(exportRequest, 'account_search.csv', 'Could not export accounts.')
